import ImageIcon from '@mui/icons-material/Image'
import InsertChartIcon from '@mui/icons-material/InsertChart'
import TableChartIcon from '@mui/icons-material/TableChart'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import TextureRoundedIcon from '@mui/icons-material/TextureRounded'
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import { ALL_REPORT_COLUMN_TYPES, ALL_REPORT_SECTION_TYPES, IReportSection, ISectionColumn, ReportSectionType, SectionColumnSettingsMap, SectionColumnType } from '../../definitions/report_builder/ReportBuilderDefinitions'
import ChartEditor from './column_editors/ChartEditor'
import ImageEditor from './column_editors/ImageEditor'
import TableEditor from './column_editors/TableEditor'
import TextEditor from './column_editors/TextEditor'

interface P {
    section: IReportSection
    index: number
    onChange: (section: IReportSection) => void
}

const SectionDetailEditor = ({ section, index, onChange }: P) => {
    if (!section) return null

    const handleTypeChange = (type: ReportSectionType) => {
        const count = type === '1-column' ? 1 : type === '2-columns' ? 2 : type === '3-columns' ? 3 : 0

        const newColumns = Array(count).fill(0).map((_, i) => {
            if (section.columns[i]) {
                return { ...section.columns[i] }
            }
            return {
                uuid: crypto.randomUUID(),
                type: 'text' as SectionColumnType,
                settings: { content: '' }
            }
        })

        const newSection = {
            ...section,
            type,
            columns: newColumns
        }
        onChange?.(newSection)
    }

    const handleColumnNameChange = (columnIndex: number, newName: string) => {
        const newSection = {
            ...section,
            columns: section.columns.map((col, i) => {
                if (i === columnIndex) {
                    return {
                        ...col,
                        name: newName
                    }
                }
                return col
            })
        }
        onChange?.(newSection)
    }

    const handleColumnTypeChange = (columnIndex: number, type: SectionColumnType) => {
        const newSection = {
            ...section,
            columns: section.columns.map((col, i) => {
                if (i === columnIndex) {
                    const settings = type === 'text'
                        ? { content: '' }
                        : type === 'image'
                            ? { url: '', height: 200, alignment: 'center' }
                            : type === 'table'
                                ? { columns: [], rows: [] }
                                : type === 'spacer'
                                    ? {}
                                    : { columns: [], rows: [] }

                    return {
                        ...col,
                        type,
                        settings
                    }
                }
                return col
            })
        }
        onChange?.(newSection)
    }

    const handleColumnSettingsChange = (columnIndex: number, settings: SectionColumnSettingsMap[SectionColumnType]) => {
        const newSection = {
            ...section,
            columns: section.columns.map((col, i) => {
                if (i === columnIndex) {
                    return {
                        ...col,
                        settings
                    }
                }
                return col
            })
        }
        onChange?.(newSection)
    }

    const renderColumnEditor = (column: ISectionColumn, index: number) => {
        const props = {
            column,
            onChange: (settings: SectionColumnSettingsMap[typeof column.type]) => handleColumnSettingsChange(index, settings)
        }

        switch (column.type) {
            case 'text':
                return <TextEditor {...props} />
            case 'image':
                return <ImageEditor {...props} />
            case 'table':
                return <TableEditor {...props} />
            case 'chart':
                return <ChartEditor />
            case 'spacer':
                return <Box sx={{ p: 2, height: 100, display: 'flex' }} />
            default:
                return null
        }
    }

    const getColumnTypeIcon = (type: SectionColumnType) => {
        switch (type) {
            case 'text':
                return <TextFieldsIcon fontSize='small' />
            case 'image':
                return <ImageIcon fontSize='small' />
            case 'table':
                return <TableChartIcon fontSize='small' />
            case 'chart':
                return <InsertChartIcon fontSize='small' />
            case 'spacer':
                return <TextureRoundedIcon fontSize='small' />
            default:
                return null
        }
    }

    return (
        <div style={{ padding: 16 }}>
            <Grid container spacing={2}>
                <Box flex={1} flexDirection='row' display='flex' marginX={2} paddingY={2} borderBottom='1px solid gray'>
                    <Typography flex={1} variant='h5'>Section {index + 1}</Typography>
                    <Box>
                        <Stack direction='row' spacing={1} className='section-type-stack'>
                            {ALL_REPORT_SECTION_TYPES.map(type => (
                                <Button
                                    variant={section.type === type ? 'contained' : 'outlined'}
                                    color={section.type === type ? 'primary' : 'secondary'}
                                    key={type}
                                    onClick={() => handleTypeChange(type)}>
                                    <Box sx={{ width: 30, height: 20, display: 'flex', flexDirection: 'row', p: 0, m: 0 }}>
                                        <Box sx={{ flex: 1, p: 0, m: 0.1, bgcolor: type === section.type ? '#fff' : 'primary.main', borderRadius: 0.4 }} />
                                        {(type === '2-columns' || type === '3-columns') && <Box sx={{ flex: 1, p: 0, m: 0.1, bgcolor: type === section.type ? '#fff' : 'primary.main', borderRadius: 0.4 }} />}
                                        {type === '3-columns' && <Box sx={{ flex: 1, p: 0, m: 0.1, bgcolor: type === section.type ? '#fff' : 'primary.main', borderRadius: 0.4 }} />}
                                    </Box>
                                </Button>
                            ))}
                        </Stack>
                    </Box>
                </Box>
                {section.columns.map((column, index) => (
                    <Grid item xs={12} key={column.uuid}>
                        <Box sx={{ marginBottom: 1, borderRadius: 2, boxShadow: 2, bgcolor: 'background.default', p: 3 }}>
                            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'row' }}>
                                <TextField
                                    variant='standard'
                                    value={column.name ?? `Content ${index + 1}`}
                                    onChange={e => handleColumnNameChange(index, e.target.value)}
                                    sx={{
                                        '& .MuiInput-underline:before': {
                                            borderBottomColor: 'transparent'
                                        },
                                        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                            borderBottomColor: 'primary.main'
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '1.25rem',
                                            fontWeight: 500,
                                            padding: '4px 0'
                                        }
                                    }}
                                    InputProps={{
                                        disableUnderline: false
                                    }}
                                />
                                <FormControl size='small' sx={{ minWidth: 180 }}>
                                    <InputLabel>Content Type</InputLabel>
                                    <Select
                                        value={column.type}
                                        label='Content type'
                                        onChange={e => handleColumnTypeChange(index, e.target.value as SectionColumnType)}
                                        renderValue={selected => (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {getColumnTypeIcon(selected as SectionColumnType)}
                                                <Box sx={{ ml: 1 }}>{selected}</Box>
                                            </Box>
                                        )}>
                                        {ALL_REPORT_COLUMN_TYPES.map(type => (
                                            <MenuItem key={type} value={type} sx={{ gap: 1 }}>
                                                {getColumnTypeIcon(type)}
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ marginTop: 2 }}>
                                {renderColumnEditor(column, index)}
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </div>
    )
}

export default SectionDetailEditor
